import moment from 'moment';

const vacio = 'No registrado';
const escapar = (valor) => String(valor == null ? '' : valor)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');
const texto = (valor) => escapar(valor || vacio);
const fecha = (valor) => (valor && moment(valor).isValid()
  ? moment(valor).format('DD/MM/YYYY') : vacio);
const numero = (valor) => (valor !== null && valor !== undefined && valor !== ''
  && Number.isFinite(Number(valor)) ? Number(valor) : null);
const porcentaje = (valor) => {
  const valorNumerico = numero(valor);
  if (valorNumerico === null) return vacio;
  const acotado = Math.min(100, Math.max(0, valorNumerico));
  return `${acotado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};
const moneda = (valor) => (numero(valor) === null ? vacio : numero(valor).toLocaleString('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2,
}));
const campo = (etiqueta, valor) => `<div class="rf-campo"><dt>${escapar(etiqueta)}</dt><dd>${texto(valor)}</dd></div>`;
const urlSegura = (valor) => (/^https?:\/\//i.test(valor || '') ? escapar(valor) : '');

function construirArbolPartidas(partidas = [], catalogoConceptos = []) {
  let secuenciaEncabezado = 0;
  const catalogo = Array.isArray(catalogoConceptos) ? catalogoConceptos : [];
  const partidasReporte = Array.isArray(partidas) ? partidas : [];
  const catalogoPorId = new Map();
  catalogo.forEach((concepto, indice) => {
    if (concepto.id !== null && concepto.id !== undefined) {
      catalogoPorId.set(String(concepto.id), { concepto, indice });
    }
  });

  const raices = [];
  const padresRaiz = new Map();
  partidasReporte.forEach((partida, indiceReporte) => {
    const idPartida = partida.idPartida !== null && partida.idPartida !== undefined
      ? partida.idPartida : (partida.partida && partida.partida.id);
    const registroCatalogo = catalogoPorId.get(String(idPartida));
    if (!registroCatalogo) {
      raices.push({
        tipoNodo: 'partida',
        partida,
        orden: Number.POSITIVE_INFINITY,
        indice: catalogo.length + indiceReporte,
      });
      return;
    }

    const ancestros = [];
    const visitados = new Set([String(registroCatalogo.concepto.id)]);
    let actual = registroCatalogo.concepto;
    while (actual.idPadre !== null && actual.idPadre !== undefined) {
      const registroPadre = catalogoPorId.get(String(actual.idPadre));
      if (!registroPadre || visitados.has(String(registroPadre.concepto.id))) break;
      ancestros.unshift(registroPadre);
      visitados.add(String(registroPadre.concepto.id));
      actual = registroPadre.concepto;
    }

    if (!ancestros.length) {
      raices.push({
        tipoNodo: 'partida',
        partida,
        conceptoCatalogo: registroCatalogo.concepto,
        orden: Number(registroCatalogo.concepto.orden),
        indice: registroCatalogo.indice,
      });
      return;
    }

    let hermanos = raices;
    let padresHermanos = padresRaiz;
    ancestros.forEach(({ concepto, indice }) => {
      const idPadre = String(concepto.id);
      let nodoPadre = padresHermanos.get(idPadre);
      if (!nodoPadre) {
        nodoPadre = {
          tipoNodo: 'padre',
          concepto,
          orden: Number(concepto.orden),
          indice,
          hijos: [],
          padresHijos: new Map(),
          idEncabezado: `rf-padre-${secuenciaEncabezado}`,
        };
        secuenciaEncabezado += 1;
        padresHermanos.set(idPadre, nodoPadre);
        hermanos.push(nodoPadre);
      }
      hermanos = nodoPadre.hijos;
      padresHermanos = nodoPadre.padresHijos;
    });

    hermanos.push({
      tipoNodo: 'partida',
      partida,
      conceptoCatalogo: registroCatalogo.concepto,
      orden: Number(registroCatalogo.concepto.orden),
      indice: registroCatalogo.indice,
    });
  });

  const ordenarNodos = (nodos) => {
    nodos.sort((a, b) => {
      const ordenA = Number.isFinite(a.orden) ? a.orden : Number.POSITIVE_INFINITY;
      const ordenB = Number.isFinite(b.orden) ? b.orden : Number.POSITIVE_INFINITY;
      return (ordenA - ordenB) || (a.indice - b.indice);
    });
    nodos.forEach((nodo) => {
      if (nodo.tipoNodo === 'padre') ordenarNodos(nodo.hijos);
    });
    return nodos;
  };

  return ordenarNodos(raices);
}

function etiquetaConcepto(concepto = {}) {
  return [concepto.clave, concepto.concepto].filter(Boolean).join(' · ') || 'Partida sin descripción';
}

export function prepararReporteFisico(contrato, reporte) {
  const corte = reporte.fecha ? moment(reporte.fecha).endOf('day') : null;
  const convenios = (contrato.convenios || []).filter((convenio) => corte && corte.isValid()
    && convenio.fecha && moment(convenio.fecha).isValid()
    && moment(convenio.fecha).isSameOrBefore(corte));
  const importeConvenios = convenios.reduce(
    (suma, convenio) => suma + (numero(convenio.importe) || 0), 0,
  );
  const conFecha = (clave) => convenios.filter((convenio) => convenio[clave])
    .sort((a, b) => moment(a.fecha).diff(moment(b.fecha)));
  const inicios = conFecha('fechaInicio');
  const terminos = conFecha('fechaTermino');
  const responsables = contrato.responsables || [];
  const residente = responsables.find((responsable) => responsable.principal);
  const supervision = (contrato.contratosRelacionados || [])
    .filter((relacion) => relacion.esSupervisionExterna)
    .map((relacion) => (String(relacion.idContratoRelacionado) === String(contrato.id)
      ? relacion.contrato : relacion.contratoRelacionado))
    .filter(Boolean)
    .map((relacion) => relacion.contratista && relacion.contratista.nombreRazonSocial);
  const avanceOriginal = numero(reporte.avanceFisico);
  const avance = avanceOriginal === null ? null : Math.min(100, Math.max(0, avanceOriginal));
  const programado = numero(reporte.avanceProgramado);
  return {
    convenios,
    importeConvenios,
    importeTotal: numero(contrato.importe) === null
      ? null : numero(contrato.importe) + importeConvenios,
    inicioConvenido: inicios.length
      ? fecha(inicios[inicios.length - 1].fechaInicio) : 'Sin convenio de plazo',
    terminoConvenido: terminos.length
      ? fecha(terminos[terminos.length - 1].fechaTermino) : 'Sin convenio de plazo',
    residente: residente && residente.funcionario ? residente.funcionario.nombre : vacio,
    superintendente: (contrato.superintendentes || []).map((item) => item.nombreCompleto).filter(Boolean).join(', ') || vacio,
    supervision: supervision.filter(Boolean).join(', ') || 'Sin supervisión externa registrada',
    diferencia: avance === null || programado === null ? null : avance - programado,
  };
}

export const estilosReporteFisico = `
.rf-documento { color: var(--rf-primary); background: #fff; font: 14px/1.55 Roboto, Arial, sans-serif; padding: 40px; max-width: 1100px; margin: 0 auto; overflow-wrap: anywhere; }
.rf-documento * { box-sizing: border-box; }
.rf-documento ::selection { background: var(--rf-selected); color: var(--rf-primary); }
.rf-documento table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.rf-documento td, .rf-documento th { padding: 0; text-align: left; font-weight: normal; }
.rf-documento h1, .rf-documento h2, .rf-documento h3, .rf-documento p, .rf-documento dl, .rf-documento figure { margin: 0; }
.rf-institucion { display: grid; grid-template-columns: 145px 1fr; gap: 28px; align-items: center; padding-bottom: 24px; }
.rf-logo { width: 145px; max-height: 80px; object-fit: contain; }
.rf-institucion-texto { text-align: center; min-width: 0; }
.rf-institucion-texto strong { display: block; font-size: 14px; line-height: 1.4; }
.rf-institucion-texto span { display: block; font-size: 12px; margin-top: 4px; }
.rf-titulo { display: flex; justify-content: space-between; align-items: baseline; gap: 20px; border-top: 2px solid var(--rf-primary); padding: 18px 0; }
.rf-titulo h1 { font-size: 24px; font-weight: 600; line-height: 1.3; letter-spacing: -.02em; }
.rf-corte { text-align: right; flex-shrink: 0; font-variant-numeric: tabular-nums; }
.rf-corte span { display: block; font-size: 12px; color: #535363; }
.rf-corte strong { font-size: 15px; }
.rf-referencia { display: none; }
.rf-identificacion { padding-bottom: 24px; border-bottom: 1px solid var(--rf-base4); }
.rf-identificacion > .rf-datos { margin-top: 12px; }
.rf-campo { min-width: 0; }
.rf-campo dt { color: #535363; font-size: 12px; margin-bottom: 3px; }
.rf-campo dd { margin: 0; font-weight: 500; }
.rf-datos { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px 24px; }
.rf-plazos { grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 20px !important; }
.rf-seccion { padding-top: 28px; }
.rf-seccion h2 { font-size: 18px; font-weight: 600; margin-bottom: 16px; }
.rf-avance { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
.rf-avance-item { padding: 14px 18px; background: var(--rf-base5); }
.rf-avance-item span { font-size: 13px; }
.rf-avance-item strong { display: block; font-size: 28px; font-weight: 500; font-variant-numeric: tabular-nums; }
.rf-comparacion { margin-top: 10px !important; font-size: 13px; }
.rf-nota { font-size: 12px; color: #535363; margin-top: 12px !important; }
.rf-observaciones { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
.rf-prosa { white-space: pre-wrap; color: #343441; max-width: 75ch; }
.rf-problema { padding: 12px 16px; background: var(--rf-naranja2); }
.rf-partida { padding: 22px 0; border-top: 1px solid var(--rf-base4); }
.rf-partidas-arbol { margin-top: 4px; }
.rf-partida-grupo { margin-top: 14px; }
.rf-partida-grupo--nivel-1 { margin-top: 20px; padding-top: 12px; border-top: 1px solid var(--rf-base4); }
.rf-partida-grupo:not(.rf-partida-grupo--nivel-1) { margin-top: 8px; padding-left: 16px; }
.rf-partida-grupo-titulo { margin: 0 0 5px !important; color: var(--rf-primary); font-size: 14px; font-weight: 600; line-height: 1.35; break-after: avoid; page-break-after: avoid; }
.rf-partida-grupo--nivel-1 > .rf-partida-grupo-titulo { font-size: 15px; }
.rf-partida--arbol { padding: 8px 0; border-top: 0; }
.rf-partida-titulo { display: flex; align-items: baseline; justify-content: space-between; gap: 20px; margin-bottom: 10px; }
.rf-partida-encabezado { margin: 0; font-size: 16px; font-weight: 600; }
.rf-partida-porcentaje { font-size: 17px; font-weight: 600; flex-shrink: 0; font-variant-numeric: tabular-nums; }
.rf-partida-fecha { font-size: 12px; color: #535363; margin-bottom: 10px !important; }
.rf-fotos { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
.rf-foto { break-inside: avoid; }
.rf-foto-imagen { position: relative; }
.rf-imagen-cargando::after {
 content: ''; position: absolute; inset: 0; background: var(--rf-base5);
 animation: rf-carga-foto 1.6s ease-in-out infinite;
}
@keyframes rf-carga-foto { 50% { background: var(--rf-base4); } }
@media (prefers-reduced-motion: reduce) { .rf-imagen-cargando::after { animation: none; } }
.rf-foto img { display: block; width: 100%; height: 230px; object-fit: contain; background: #f5f5f7; }
.rf-foto figcaption { font-size: 12px; margin-top: 6px; color: #535363; }
.rf-sin-fotos { margin-top: 12px !important; color: #535363; font-size: 12px; }
.rf-pie { border-top: 1px solid var(--rf-base4); margin-top: 28px; padding-top: 12px; color: #535363; font-size: 11px; }
@media screen and (max-width: 600px) {
 .rf-documento { padding: 22px 18px; font-size: 14px; }
 .rf-institucion { grid-template-columns: 1fr; gap: 14px; }
 .rf-logo { margin: auto; }
 .rf-titulo { display: block; }
 .rf-titulo h1 { font-size: 22px; }
 .rf-corte { text-align: left; margin-top: 12px; }
 .rf-datos, .rf-plazos, .rf-observaciones { grid-template-columns: 1fr; gap: 16px; }
 .rf-avance { gap: 12px; }
 .rf-avance-item { padding: 12px; }
 .rf-avance-item strong { font-size: 24px; }
 .rf-fotos { grid-template-columns: 1fr; }
 .rf-partida-grupo:not(.rf-partida-grupo--nivel-1) { padding-left: 12px; }
}
@page { size: letter portrait; margin: 12mm; }
@media print {
 html, body, #app, .v-application, .v-application--wrap { background: white !important; min-height: 0 !important; }
 .rf-documento { max-width: none; padding: 0; font-size: 10pt; line-height: 1.4; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
 .rf-documento thead { display: table-header-group; break-inside: avoid; }
 .rf-institucion { padding-bottom: 12px; gap: 18px; grid-template-columns: 120px 1fr; }
 .rf-logo { width: 120px; max-height: 60px; }
 .rf-institucion-texto strong { font-size: 10pt; }
 .rf-institucion-texto span { font-size: 8pt; }
 .rf-titulo { padding: 12px 0; }
 .rf-titulo h1 { font-size: 17pt; }
 .rf-referencia { display: block; padding-bottom: 10px; font-size: 8pt; }
 .rf-identificacion { padding-bottom: 14px; }
 .rf-seccion { padding-top: 18px; }
 .rf-seccion h2 { font-size: 13pt; break-after: avoid; }
 .rf-datos { gap: 12px 18px; }
 .rf-avance-item strong { font-size: 22pt; }
 .rf-partida { padding: 16px 0; break-inside: avoid; }
 .rf-partida-titulo { break-after: avoid; }
 .rf-partida-grupo--nivel-1 { margin-top: 14px; padding-top: 10px; }
 .rf-partida-grupo:not(.rf-partida-grupo--nivel-1) { padding-left: 12px; }
 .rf-partida-grupo-titulo { font-size: 9pt; }
 .rf-partida-grupo--nivel-1 > .rf-partida-grupo-titulo { font-size: 10pt; }
 .rf-partida-encabezado { font-size: 10pt; }
 .rf-foto img { height: 55mm; }
 .rf-imagen-cargando::after { display: none !important; }
 .rf-foto, .rf-avance, .rf-campo, .rf-institucion, .rf-titulo { break-inside: avoid; }
 .rf-prosa { orphans: 3; widows: 3; }
 .rf-pie { margin-top: 18px; }
}
`;

export function crearDocumentoReporteFisico({
  contrato, reporte, encabezado, colores, servidorArchivos, catalogoConceptos = [],
}) {
  const datos = prepararReporteFisico(contrato, reporte);
  const variables = ['primary', 'secondary', 'selected', 'base4', 'base5', 'naranja2']
    .map((clave) => `--rf-${clave}:${/^#[0-9a-f]{3,8}$/i.test(colores[clave]) ? colores[clave] : '#05053e'}`).join(';');
  const diferencia = datos.diferencia === null ? 'Comparación no disponible.'
    : `${Math.abs(datos.diferencia).toLocaleString('es-MX', { maximumFractionDigits: 2 })} puntos porcentuales ${datos.diferencia < 0 ? 'por debajo' : 'por encima'} del avance programado.`;
  const renderPartida = (partida, conceptoCatalogo, indiceReporte, nivel) => {
    const concepto = (partida.partida && partida.partida.concepto)
      || (conceptoCatalogo && conceptoCatalogo.concepto) || 'Partida sin descripción';
    const clave = (partida.partida && partida.partida.clave)
      || (conceptoCatalogo && conceptoCatalogo.clave);
    const fotos = (partida.imagenes || []).filter((imagen) => typeof imagen === 'string' && imagen);
    const nivelEncabezado = Math.min(2 + nivel, 6);
    return `<article class="rf-partida rf-partida--arbol">
      <div class="rf-partida-titulo"><h${nivelEncabezado} class="rf-partida-encabezado">${texto(clave ? `${clave} · ${concepto}` : concepto)}</h${nivelEncabezado}><span class="rf-partida-porcentaje">${porcentaje(partida.avanceFisico)}</span></div>
      ${partida.fecha ? `<p class="rf-partida-fecha">Fecha de actividad: ${fecha(partida.fecha)}</p>` : ''}
      ${partida.comentario ? `<p class="rf-prosa">${escapar(partida.comentario)}</p>` : ''}
      ${fotos.length ? `<div class="rf-fotos">${fotos.map((imagen, fotoIndice) => `<figure class="rf-foto"><div class="rf-foto-imagen"><img src="${urlSegura(`${servidorArchivos.replace(/\/$/, '')}/${encodeURIComponent(imagen)}`)}" alt="${escapar(`Evidencia fotográfica de ${concepto}, fotografía ${fotoIndice + 1}`)}"></div><figcaption>Fotografía ${indiceReporte + 1}.${fotoIndice + 1} · ${texto(concepto)}</figcaption></figure>`).join('')}</div>` : '<p class="rf-sin-fotos">Sin evidencia fotográfica registrada.</p>'}
    </article>`;
  };
  let indiceReporte = 0;
  const renderArbol = (nodos, nivel = 1) => nodos.map((nodo) => {
    if (nodo.tipoNodo === 'partida') {
      const html = renderPartida(nodo.partida, nodo.conceptoCatalogo, indiceReporte, nivel);
      indiceReporte += 1;
      return html;
    }
    const nivelEncabezado = Math.min(2 + nivel, 6);
    return `<div class="rf-partida-grupo rf-partida-grupo--nivel-${Math.min(nivel, 6)}" role="group" aria-labelledby="${nodo.idEncabezado}">
      <h${nivelEncabezado} id="${nodo.idEncabezado}" class="rf-partida-grupo-titulo">${texto(etiquetaConcepto(nodo.concepto))}</h${nivelEncabezado}>
      ${renderArbol(nodo.hijos, nivel + 1)}
    </div>`;
  }).join('');
  const arbolPartidas = construirArbolPartidas(
    reporte.partidas || [], catalogoConceptos,
  );
  const partidas = renderArbol(arbolPartidas);
  return `<style>${estilosReporteFisico}</style><article class="rf-documento" style="${variables}">
    <table class="rf-tabla-documento" role="presentation"><thead><tr><th>
      <header>
        <div class="rf-institucion"><img class="rf-logo" src="${urlSegura(encabezado.logo)}" alt="H. Ayuntamiento de Hermosillo"><div class="rf-institucion-texto"><strong>${texto(encabezado.titulo1)}</strong><span>${texto(encabezado.titulo2)}</span><span>${texto(encabezado.titulo3)}</span></div></div>
        <div class="rf-titulo"><h1>Reporte de avance físico ${texto(reporte.numero)}</h1><div class="rf-corte"><span>Fecha de corte</span><strong>${fecha(reporte.fecha)}</strong></div></div>
        <p class="rf-referencia">Contrato ${texto(contrato.numero)}</p>
      </header>
    </th></tr></thead><tbody><tr><td>
        <dl class="rf-identificacion">${campo('Obra', contrato.meta)}<div class="rf-datos">${campo('Contrato', contrato.numero)}${campo('Fecha del contrato', fecha(contrato.fecha))}${campo('Contratista', contrato.contratista && contrato.contratista.nombreRazonSocial)}</div></dl>
      <section class="rf-seccion"><h2>Avance a la fecha de corte</h2><div class="rf-avance"><div class="rf-avance-item"><span>Programado</span><strong>${porcentaje(reporte.avanceProgramado)}</strong></div><div class="rf-avance-item"><span>Físico</span><strong>${porcentaje(reporte.avanceFisico)}</strong></div></div><p class="rf-comparacion">${escapar(datos.diferencia === 0 ? 'El avance físico coincide con el programado.' : diferencia)}</p></section>
      <section class="rf-seccion"><h2>Importes y plazos</h2><dl class="rf-datos">${campo('Importe del contrato', moneda(contrato.importe))}${campo('Convenios a la fecha de corte', moneda(datos.importeConvenios))}${campo('Total contratado', moneda(datos.importeTotal))}</dl><dl class="rf-datos rf-plazos">${campo('Inicio del contrato', fecha(contrato.fechaInicio))}${campo('Fecha de inicio convenida', datos.inicioConvenido)}${campo('Término del contrato', fecha(contrato.fechaTermino))}${campo('Fecha de término convenida', datos.terminoConvenido)}</dl><p class="rf-nota">Importes en MXN, sin IVA. Convenios con fecha igual o anterior al corte del reporte.</p></section>
      <section class="rf-seccion"><h2>Responsables</h2><dl class="rf-datos">${campo('Residente / responsable principal', datos.residente)}${campo('Superintendente', datos.superintendente)}${campo('Supervisión externa', datos.supervision)}</dl></section>
      <div class="rf-observaciones rf-seccion"><section><h2>Comentarios generales</h2><p class="rf-prosa">${texto(reporte.comentarios || 'Sin comentarios registrados.')}</p></section><section><h2>Problemática</h2><p class="rf-prosa ${reporte.existeProblema ? 'rf-problema' : ''}">${texto(reporte.existeProblema ? (reporte.descripcionProblema || 'Problemática registrada sin descripción.') : 'No hay problemática registrada.')}</p></section></div>
      <section class="rf-seccion"><h2>Avance por partida y evidencia fotográfica</h2><div class="rf-partidas-arbol">${partidas || '<p class="rf-prosa">No hay partidas registradas en este reporte.</p>'}</div></section>
      <footer class="rf-pie">Contrato ${texto(contrato.numero)} · Reporte físico ${texto(reporte.numero)} · Fecha de corte ${fecha(reporte.fecha)}</footer>
    </td></tr></tbody></table>
  </article>`;
}
